import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { itemIconPath } from '../compositor/assetPaths';
import {
  DOCK_POS,
  DRAG_SLOP,
  isFoodGhostTarget,
  isFoodHintTarget,
  isMouseLike,
  prefersFinePointer,
  type PlaceFoodItem,
} from './placeFood';
import { sfx } from './sfx';
import { clampToBand, clientToStagePct, type Point } from './wanderMath';

type Drag = {
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
  fromGhost: boolean;
};

export function FoodPlaceLayer(props: {
  item: PlaceFoodItem | null;
  drop: { code: string; pos: Point } | null;
  eating?: boolean;
  onDrop: (pos: Point) => void;
  onCancel: () => void;
}) {
  const { item, drop, eating, onDrop, onCancel } = props;
  const layerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const [ghostPos, setGhostPos] = useState<Point>(DOCK_POS);
  const [docked, setDocked] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [fine, setFine] = useState(false);

  function pctFromClient(clientX: number, clientY: number): Point | null {
    const scene = layerRef.current?.closest('.habitat-scene');
    if (!scene) return null;
    const rect = scene.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return clientToStagePct(clientX, clientY, rect);
  }

  function dropAt(clientX: number, clientY: number): boolean {
    const pct = pctFromClient(clientX, clientY);
    if (!pct) return false;
    onDropRef.current(clampToBand(pct));
    return true;
  }

  useEffect(() => {
    if (!item) return;
    const mouseFollow = prefersFinePointer();
    setFine(mouseFollow);
    setDocked(!mouseFollow);
    setGhostPos(mouseFollow ? { x: 50, y: 72 } : DOCK_POS);
    setDragging(false);
    dragRef.current = null;

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (mouseFollow && isMouseLike(e) && !drag) {
        if (!isFoodHintTarget(e.target)) {
          const pct = pctFromClient(e.clientX, e.clientY);
          if (pct) {
            setDocked(false);
            setGhostPos(pct);
          }
        }
        return;
      }
      if (!drag || drag.pointerId !== e.pointerId) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && (dx * dx + dy * dy) >= DRAG_SLOP) {
        drag.moved = true;
        setDragging(true);
        sfx.grab();
      }
      const pct = pctFromClient(e.clientX, e.clientY);
      if (pct) {
        setDocked(false);
        setGhostPos(pct);
      }
    };

    const onUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const { moved, fromGhost } = drag;
      dragRef.current = null;
      setDragging(false);
      if (e.type === 'pointercancel') {
        if (!mouseFollow) {
          setDocked(true);
          setGhostPos(DOCK_POS);
        }
        return;
      }
      if (moved || !fromGhost || isMouseLike(e)) {
        if (!dropAt(e.clientX, e.clientY) && !mouseFollow) {
          setDocked(true);
          setGhostPos(DOCK_POS);
        }
        return;
      }
      if (!mouseFollow) {
        setDocked(true);
        setGhostPos(DOCK_POS);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [item]);

  function startDrag(e: ReactPointerEvent<HTMLElement>, fromGhost: boolean) {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      fromGhost,
    };
    const pct = pctFromClient(e.clientX, e.clientY);
    if (pct) {
      setDocked(false);
      setGhostPos(pct);
    }
    if (!fine || !isMouseLike(e)) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    }
  }

  function handleLayerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!item) return;
    if (isFoodHintTarget(e.target)) return;
    if (fine && isMouseLike(e)) {
      e.preventDefault();
      e.stopPropagation();
      dropAt(e.clientX, e.clientY);
      return;
    }
    startDrag(e, isFoodGhostTarget(e.target));
  }

  const layerClass = [
    'habitat-food-layer',
    item ? 'placing-food' : undefined,
    item && fine ? 'placing-food-mouse' : undefined,
  ].filter(Boolean).join(' ');

  const ghostClass = [
    'habitat-food-ghost',
    fine ? 'mouse-follow' : undefined,
    dragging ? 'dragging' : undefined,
    docked && !fine ? 'docked' : undefined,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={layerRef}
      className={layerClass}
      onPointerDown={handleLayerDown}
    >
      {item && (
        <div className="habitat-food-hint">
          <span className="habitat-food-hint-text">
            Drag {item.name} onto the floor, or tap to drop it!
          </span>
          <button
            type="button"
            className="chip habitat-food-cancel"
            onPointerUp={(e) => e.stopPropagation()}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      )}
      {item && (
        <img
          className={ghostClass}
          src={itemIconPath(item.code)}
          alt=""
          style={docked && !fine ? undefined : { left: `${ghostPos.x}%`, top: `${ghostPos.y}%` }}
        />
      )}
      {drop && (
        <img
          className={eating ? 'habitat-food-drop eaten' : 'habitat-food-drop'}
          src={itemIconPath(drop.code)}
          alt=""
          style={{ left: `${drop.pos.x}%`, top: `${drop.pos.y}%` }}
        />
      )}
    </div>
  );
}
